import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import dayjs from "dayjs"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { usePeriod } from "~/ui-rtk/hooks/period"
import { getPersonaIcon } from "./personaConfig"

interface SuggestionCard {
  icon: string
  title: string
  description: string
  prompt: string
}

type SuggestionSet = readonly SuggestionCard[]

const SET_KEYS = [
  "home",
  "paid",
  "organic",
  "creative",
  "revenue",
  "brand",
] as const

/** Each persona sees suggestion categories ordered by relevance to their role. */
const PERSONA_SET_ORDERS: Record<string, readonly string[]> = {
  cmo: ["home", "paid", "brand", "revenue", "organic", "creative"],
  growth: ["paid", "revenue", "home", "creative", "organic", "brand"],
  paidmedia: ["paid", "creative", "home", "revenue", "organic", "brand"],
  ecom: ["revenue", "paid", "creative", "home", "organic", "brand"],
  brand: ["brand", "organic", "home", "paid", "creative", "revenue"],
  cro: ["revenue", "paid", "creative", "home", "organic", "brand"],
  retention: ["home", "revenue", "organic", "paid", "creative", "brand"],
}

const CYCLE_INTERVAL_MS = 15000
const FADE_DURATION_MS = 250

function buildSuggestions(dateRange: string): Record<string, SuggestionSet> {
  return {
    home: [
      {
        icon: "chart-bar",
        title: "Performance Summary",
        description: "Key metrics and KPIs across all channels",
        prompt: `Give me a performance summary across all channels ${dateRange}. Include spend, revenue, ROAS, and any notable trends.`,
      },
      {
        icon: "currency",
        title: "Channel ROAS",
        description: "Return on ad spend by channel",
        prompt: `Compare ROAS across all advertising channels ${dateRange}. Which channels are most and least efficient?`,
      },
      {
        icon: "trophy",
        title: "Top Campaigns",
        description: "Best performing campaigns",
        prompt: `What are the top 5 performing campaigns ${dateRange} by revenue? Include spend and ROAS for each.`,
      },
      {
        icon: "arrows",
        title: "Month vs Prior",
        description: "Period-over-period comparison",
        prompt: `Compare this period's performance ${dateRange} against the prior period. Highlight improvements and declines.`,
      },
    ],
    paid: [
      {
        icon: "currency",
        title: "Spend Efficiency",
        description: "Cost efficiency across campaigns",
        prompt: `Analyze spend efficiency across all paid campaigns ${dateRange}. Where are we over-spending or under-spending?`,
      },
      {
        icon: "trophy",
        title: "Top Campaigns",
        description: "Highest ROAS campaigns",
        prompt: `Show the top performing paid campaigns ${dateRange} ranked by ROAS. Include spend, revenue, and CPA.`,
      },
      {
        icon: "layers",
        title: "Platform Breakdown",
        description: "Performance by ad platform",
        prompt: `Break down paid media performance by platform (Meta, Google, TikTok) ${dateRange}. Compare spend, impressions, and conversions.`,
      },
      {
        icon: "adjustments",
        title: "Budget Allocation",
        description: "Optimal budget distribution",
        prompt: `Based on current performance ${dateRange}, how should we reallocate budget across platforms for better ROAS?`,
      },
    ],
    organic: [
      {
        icon: "trending",
        title: "Engagement Trends",
        description: "Likes, comments, and shares over time",
        prompt: `Show organic engagement trends ${dateRange}. How have likes, comments, and shares changed over time?`,
      },
      {
        icon: "star",
        title: "Top Posts",
        description: "Most engaging content",
        prompt: `What are the top organic posts by engagement ${dateRange}? What content themes are resonating?`,
      },
      {
        icon: "layers",
        title: "Platform Comparison",
        description: "Organic reach across platforms",
        prompt: `Compare organic performance across platforms ${dateRange}. Which platforms drive the most engagement and reach?`,
      },
      {
        icon: "clock",
        title: "Posting Frequency",
        description: "Content cadence analysis",
        prompt: `Analyze our posting frequency ${dateRange}. Are we posting enough? How does frequency correlate with engagement?`,
      },
    ],
    creative: [
      {
        icon: "trending",
        title: "Best CTR Creatives",
        description: "Highest click-through rate ads",
        prompt: `Which ad creatives have the highest CTR ${dateRange}? What visual or copy patterns stand out?`,
      },
      {
        icon: "layers",
        title: "Format Performance",
        description: "Video vs image vs carousel",
        prompt: `Compare ad format performance ${dateRange} — video vs image vs carousel. Which format delivers the best ROAS?`,
      },
      {
        icon: "alert",
        title: "Worst Performers",
        description: "Creatives that need attention",
        prompt: `Identify the worst performing ad creatives ${dateRange}. Which ones should be paused or refreshed?`,
      },
      {
        icon: "arrows",
        title: "Cross-Platform Creative",
        description: "Same creative, different results",
        prompt: `Are there creatives running on multiple platforms ${dateRange}? Compare their performance across platforms.`,
      },
    ],
    revenue: [
      {
        icon: "funnel",
        title: "Conversion Funnel",
        description: "Funnel drop-off analysis",
        prompt: `Analyze the conversion funnel ${dateRange}. Where are the biggest drop-offs between visit, add-to-cart, and purchase?`,
      },
      {
        icon: "cursor",
        title: "Landing Pages",
        description: "Top and bottom performing pages",
        prompt: `Which landing pages have the best and worst conversion rates ${dateRange}? What's the revenue impact?`,
      },
      {
        icon: "arrow-down",
        title: "Bounce Rate",
        description: "Pages losing visitors",
        prompt: `Which pages have the highest bounce rates ${dateRange}? How much potential revenue are we losing?`,
      },
      {
        icon: "chart-bar",
        title: "Revenue Trends",
        description: "Revenue trajectory and patterns",
        prompt: `Show revenue trends ${dateRange}. Are there any weekly patterns or anomalies worth investigating?`,
      },
    ],
    brand: [
      {
        icon: "trending",
        title: "Brand Health Trend",
        description: "Brand value over time",
        prompt: `How has our brand value score trended ${dateRange}? What factors are driving changes?`,
      },
      {
        icon: "arrows",
        title: "Competitor Comparison",
        description: "How we stack up",
        prompt: `Compare our brand metrics against competitors ${dateRange}. Where do we lead and where do we trail?`,
      },
      {
        icon: "megaphone",
        title: "Share of Voice",
        description: "Brand visibility in the market",
        prompt: `What is our share of voice ${dateRange}? How does it compare to competitors and how has it changed?`,
      },
      {
        icon: "heart",
        title: "Organic Engagement",
        description: "Unpaid brand engagement",
        prompt: `Analyze organic brand engagement ${dateRange}. How are consumers interacting with our brand outside of paid campaigns?`,
      },
    ],
  }
}

function getPageContext(pathname: string): string {
  if (pathname === "/" || pathname.startsWith("/home")) return "home"
  if (
    pathname.startsWith("/brand-media") ||
    pathname.startsWith("/campaign-management")
  ) {
    return "paid"
  }
  if (pathname.startsWith("/organic-media")) return "organic"
  if (pathname.startsWith("/creative-reports")) return "creative"
  if (
    pathname.startsWith("/revenue") ||
    pathname.startsWith("/web-merchandising")
  ) {
    return "revenue"
  }
  if (pathname.startsWith("/brand-value")) return "brand"
  return "home"
}

const ICON_PATHS: Record<string, string> = {
  "chart-bar":
    "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z",
  currency:
    "M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  trophy:
    "M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.996.144-1.708.153-2.206.163-.455.012-.843.374-.843.829 0 .842.056 1.63.16 2.394A5.255 5.255 0 0 0 5.25 10.5v.262c.151.393.35.765.587 1.11.245.357.546.673.89.931M18.75 4.236c.996.144 1.708.153 2.206.163.455.012.843.374.843.829 0 .842-.056 1.63-.16 2.394A5.255 5.255 0 0 1 18.75 10.5v.262c-.151.393-.35.765-.587 1.11a4.965 4.965 0 0 1-.89.931",
  arrows:
    "M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5",
  layers:
    "M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0L21.75 16.5 12 21.75 2.25 16.5l4.179-2.25m0 0 5.571 3 5.571-3",
  adjustments:
    "M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75",
  trending:
    "M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941",
  star: "M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z",
  clock: "M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  alert:
    "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z",
  funnel:
    "M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z",
  cursor:
    "M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59",
  "arrow-down": "M12 4.5v15m0 0 6.75-6.75M12 19.5l-6.75-6.75",
  megaphone:
    "M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46",
  heart:
    "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z",
}

function SuggestionIcon({ icon }: { icon: string }) {
  const path = ICON_PATHS[icon]
  if (!path) return null
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-4 h-4 text-brand shrink-0"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  )
}

const PERSONA_LABELS: Record<string, { title: string; subtitle: string }> = {
  cmo: {
    title: "CMO Assistant",
    subtitle: "Strategic overview, budget allocation, and brand health",
  },
  growth: {
    title: "Growth Assistant",
    subtitle: "Paid media efficiency, CAC, and ROAS analysis",
  },
  paidmedia: {
    title: "Paid Media Assistant",
    subtitle: "Ad spend optimization, attribution, and creative performance",
  },
  ecom: {
    title: "E-commerce Assistant",
    subtitle: "Product performance, conversion rates, and merchandising",
  },
  brand: {
    title: "Brand Assistant",
    subtitle: "Brand value tracking and competitive positioning",
  },
  cro: {
    title: "CRO Assistant",
    subtitle: "Funnel analysis, landing pages, and conversion optimization",
  },
  retention: {
    title: "Retention Assistant",
    subtitle: "Cohort analysis, churn, and LTV insights",
  },
}

const DEFAULT_LABEL = {
  title: "Analytics Assistant",
  subtitle:
    "Ask about your performance, revenue, organic reach, or brand health",
}

interface QuickPromptsProps {
  onSelect: (prompt: string) => void
}

export function QuickPrompts({ onSelect }: QuickPromptsProps) {
  const { pathname } = useLocation()
  const { period } = usePeriod()
  const selectedPersona = useAppSelector(
    state => state.chatbotSlice.selectedPersona,
  )

  const dateRange = useMemo(() => {
    const start = dayjs(period[0]).format("MMM D")
    const end = dayjs(period[1]).format("MMM D")
    return `from ${start} to ${end}`
  }, [period])

  const pageContext = useMemo(() => getPageContext(pathname), [pathname])

  const allSuggestions = useMemo(() => buildSuggestions(dateRange), [dateRange])

  // Persona determines category order; page context bumps its set to front
  const orderedKeys = useMemo(() => {
    const base = PERSONA_SET_ORDERS[selectedPersona] ?? [...SET_KEYS]
    // If the current page's context differs from the persona's first pick,
    // move it to the front so the user sees relevant cards immediately
    if (pageContext !== base[0] && base.includes(pageContext)) {
      return [pageContext, ...base.filter(k => k !== pageContext)]
    }
    return [...base]
  }, [selectedPersona, pageContext])

  // Cycling state
  const [activeIndex, setActiveIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset to first set when persona or page context changes
  useEffect(() => {
    setActiveIndex(0)
    setVisible(true)
  }, [selectedPersona, pageContext])

  // Use a ref so cycleNext is stable and doesn't restart the interval
  const orderedKeysRef = useRef(orderedKeys)
  useEffect(() => {
    orderedKeysRef.current = orderedKeys
  }, [orderedKeys])

  const startFade = useCallback(
    (nextIndex: number | ((prev: number) => number)) => {
      if (fadeRef.current) clearTimeout(fadeRef.current)
      setVisible(false)
      fadeRef.current = setTimeout(() => {
        setActiveIndex(nextIndex)
        setVisible(true)
      }, FADE_DURATION_MS)
    },
    [],
  )

  const cycleNext = useCallback(() => {
    startFade(prev => (prev + 1) % orderedKeysRef.current.length)
  }, [startFade])

  useEffect(() => {
    if (isHovered) {
      if (timerRef.current) clearInterval(timerRef.current)
      // Cancel any in-flight fade so cards don't jump while hovering
      if (fadeRef.current) {
        clearTimeout(fadeRef.current)
        setVisible(true)
      }
      return
    }
    timerRef.current = setInterval(cycleNext, CYCLE_INTERVAL_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (fadeRef.current) clearTimeout(fadeRef.current)
    }
  }, [isHovered, cycleNext])

  const currentKey = orderedKeys[activeIndex]
  const suggestions = allSuggestions[currentKey] ?? allSuggestions.home

  const personaIcon = getPersonaIcon(selectedPersona)
  const personaLabel = PERSONA_LABELS[selectedPersona] ?? DEFAULT_LABEL

  return (
    <div className="flex flex-col items-center justify-center h-full px-4 py-6">
      {/* Welcome section — persona-aware */}
      <div className="flex flex-col items-center gap-2 mb-6">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${personaIcon.color}20` }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke={personaIcon.color}
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={personaIcon.path}
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold">{personaLabel.title}</h3>
        <p className="text-sm opacity-50 text-center">
          {personaLabel.subtitle}&mdash;or try a suggestion below.
        </p>
      </div>

      {/* 2x2 suggestion grid with fade cycling */}
      <div
        className="w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className={`grid grid-cols-2 gap-2.5 w-full transition-all duration-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
          }`}
        >
          {suggestions.map(card => (
            <button
              key={card.title}
              type="button"
              className="bg-system-blue-600 border border-system-blue-500 hover:border-brand/50 rounded-xl p-3.5 text-left transition-colors duration-150 flex flex-col gap-1.5 group"
              onClick={() => onSelect(card.prompt)}
            >
              <div className="flex items-center gap-2">
                <SuggestionIcon icon={card.icon} />
                <span className="text-base font-medium group-hover:text-brand transition-colors">
                  {card.title}
                </span>
              </div>
              <p className="text-sm leading-relaxed opacity-50">
                {card.description}
              </p>
            </button>
          ))}
        </div>

        {/* Navigation: arrows + dot indicators */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-circle"
            onClick={() =>
              startFade(
                (activeIndex - 1 + orderedKeys.length) % orderedKeys.length,
              )
            }
            aria-label="Previous suggestions"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-3.5 h-3.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </button>

          <div className="flex items-center gap-1.5">
            {orderedKeys.map((key, i) => (
              <button
                key={key}
                type="button"
                className={`rounded-full transition-all duration-200 ${
                  i === activeIndex
                    ? "w-4 h-1.5 bg-brand"
                    : "w-1.5 h-1.5 bg-base-content/20 hover:bg-base-content/40"
                }`}
                onClick={() => startFade(i)}
                aria-label={`Show ${key} suggestions`}
              />
            ))}
          </div>

          <button
            type="button"
            className="btn btn-ghost btn-xs btn-circle"
            onClick={() => startFade((activeIndex + 1) % orderedKeys.length)}
            aria-label="Next suggestions"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-3.5 h-3.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
